import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
	providedIn: 'root',
})
export class RecentSearchService {
	private readonly STORAGE_KEY = 'ponyplace_recent_searches';
	private readonly MAX_RECENT_SEARCHES = 10;

	/**
	 * Static suggestions fallback - displayed when user has no recent searches
	 * These are hardcoded terms that you can update as needed
	 */
	private readonly staticSuggestions: string[] = [
		'Quarter Horse',
		'Mare for sale',
		'Beginner friendly',
		'Dressage horse',
		'Trail horse',
	];

	private recentSearchesSubject = new BehaviorSubject<string[]>(this.loadFromStorage());

	constructor() {}

	/**
	 * Get recent searches as an observable
	 */
	getRecentSearches$(): Observable<string[]> {
		return this.recentSearchesSubject.asObservable();
	}

	/**
	 * Get current recent searches synchronously
	 */
	getRecentSearches(): string[] {
		return this.recentSearchesSubject.getValue();
	}

	/**
	 * Add a search term to recent searches
	 * Moves to top if already exists, maintains max limit
	 */
	addRecentSearch(term: string): void {
		if (!term || !term.trim()) {
			return;
		}

		const normalizedTerm = term.trim();
		let searches = this.getRecentSearches();

		// Remove if already exists (will be added to top)
		searches = searches.filter((s) => s.toLowerCase() !== normalizedTerm.toLowerCase());

		// Add to beginning
		searches.unshift(normalizedTerm);

		// Maintain max limit
		if (searches.length > this.MAX_RECENT_SEARCHES) {
			searches = searches.slice(0, this.MAX_RECENT_SEARCHES);
		}

		this.saveToStorage(searches);
		this.recentSearchesSubject.next(searches);
	}

	/**
	 * Remove a specific search term from recent searches
	 */
	removeRecentSearch(term: string): void {
		const searches = this.getRecentSearches().filter((s) => s.toLowerCase() !== term.toLowerCase());
		this.saveToStorage(searches);
		this.recentSearchesSubject.next(searches);
	}

	/**
	 * Clear all recent searches (called on logout)
	 */
	clearRecentSearches(): void {
		this.saveToStorage([]);
		this.recentSearchesSubject.next([]);
		console.log('Recent searches cleared');
	}

	/**
	 * Get static suggestions (used when no recent searches exist)
	 */
	getStaticSuggestions(): string[] {
		return [...this.staticSuggestions];
	}

	/**
	 * Check if a term is in recent searches
	 */
	isRecentSearch(term: string): boolean {
		return this.getRecentSearches().some((s) => s.toLowerCase() === term.toLowerCase());
	}

	/**
	 * Load recent searches from localStorage
	 */
	private loadFromStorage(): string[] {
		try {
			const stored = localStorage.getItem(this.STORAGE_KEY);
			if (stored) {
				const parsed = JSON.parse(stored);
				if (Array.isArray(parsed)) {
					return parsed.filter((item) => typeof item === 'string');
				}
			}
		} catch (error) {
			console.warn('Failed to load recent searches from storage:', error);
		}
		return [];
	}

	/**
	 * Save recent searches to localStorage
	 */
	private saveToStorage(searches: string[]): void {
		try {
			localStorage.setItem(this.STORAGE_KEY, JSON.stringify(searches));
		} catch (error) {
			console.warn('Failed to save recent searches to storage:', error);
		}
	}
}






